import { TabIndexController } from "../../utils/TabIndexController.js";

/**
 * Custom Select Component
 * 네이티브 select를 커스텀 드롭다운으로 변환
 * 웹접근성 및 모바일 모달 지원
 */
export class Select {
    constructor(element) {
        this.element = element;
        this.nativeSelect = element.querySelector(".no-form-select__native");
        this.trigger = element.querySelector(".no-form-select__trigger");
        this.dropdown = element.querySelector(".no-form-select__dropdown");
        this.optionsList = element.querySelector(".no-form-select__options");
        this.valueDisplay = element.querySelector(".no-form-select__value");
        this.icon = element.querySelector(".no-form-select__icon");

        this.isOpen = false;
        this.selectedIndex = -1;
        this.focusedIndex = -1;
        this.options = [];
        this.tabIndexController = new TabIndexController();
        this.isMobile = window.matchMedia("(max-width: 768px)").matches;

        this.init();
    }

    init() {
        if (
            !this.nativeSelect ||
            !this.trigger ||
            !this.dropdown ||
            !this.optionsList
        ) {
            console.warn("Select: Required elements not found", {
                nativeSelect: !!this.nativeSelect,
                trigger: !!this.trigger,
                dropdown: !!this.dropdown,
                optionsList: !!this.optionsList,
            });
            return;
        }

        // 모바일/데스크톱 체크
        this.isMobile = window.matchMedia("(max-width: 768px)").matches;

        if (this.isMobile) {
            // 모바일: 커스텀 select 사용 (모달로 표시)
            this.nativeSelect.style.position = "absolute";
            this.nativeSelect.style.opacity = "0";
            this.nativeSelect.style.pointerEvents = "none";
            this.nativeSelect.style.width = "1px";
            this.nativeSelect.style.height = "1px";
            this.nativeSelect.style.display = "block";
            this.trigger.style.display = "flex"; // 모바일에서도 트리거 사용
            this.dropdown.style.display = "none";
        } else {
            // 데스크톱: 커스텀 select 사용
            this.nativeSelect.style.position = "absolute";
            this.nativeSelect.style.opacity = "0";
            this.nativeSelect.style.pointerEvents = "none";
            this.nativeSelect.style.width = "1px";
            this.nativeSelect.style.height = "1px";
            this.nativeSelect.style.display = "block";
            this.trigger.style.display = "flex";
            this.dropdown.style.display = "none"; // 초기에는 숨김
        }

        // 드롭다운에 Lenis 스크롤 방지 속성 추가
        if (this.dropdown) {
            this.dropdown.setAttribute("data-lenis-prevent", "");
        }

        // 옵션 파싱
        this.parseOptions();

        // 드롭다운 렌더링
        this.renderOptions();

        // 이벤트 리스너
        this.attachEvents();

        // 초기값 설정
        this.updateDisplay();
    }

    parseOptions() {
        const nativeOptions = Array.from(this.nativeSelect.options);
        this.options = nativeOptions.map((option, index) => ({
            value: option.value,
            text: option.textContent,
            disabled: option.disabled,
            selected: option.selected,
            index: index,
        }));

        // 선택된 옵션 찾기
        const selectedOption = nativeOptions.find((opt) => opt.selected);
        if (selectedOption) {
            this.selectedIndex = nativeOptions.indexOf(selectedOption);
            this.focusedIndex = this.selectedIndex;
        } else {
            this.focusedIndex = 0;
        }
    }

    renderOptions() {
        this.optionsList.innerHTML = "";

        this.options.forEach((option, index) => {
            const li = document.createElement("li");
            li.className = "no-form-select__option";
            li.setAttribute("role", "option");
            li.setAttribute("data-value", option.value);
            li.setAttribute("data-index", index);
            li.textContent = option.text;

            if (option.disabled) {
                li.setAttribute("aria-disabled", "true");
                li.classList.add("no-form-select__option--disabled");
            }

            if (option.selected) {
                li.setAttribute("aria-selected", "true");
                li.classList.add("no-form-select__option--selected");
            }

            this.optionsList.appendChild(li);
        });
    }

    attachEvents() {
        // 트리거 클릭
        this.trigger.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggle();
        });

        // 트리거 키보드 이벤트
        this.trigger.addEventListener("keydown", (e) => {
            this.handleTriggerKeydown(e);
        });

        // 옵션 클릭
        this.optionsList.addEventListener("click", (e) => {
            const option = e.target.closest(".no-form-select__option");
            if (
                option &&
                !option.classList.contains("no-form-select__option--disabled")
            ) {
                const index = parseInt(option.getAttribute("data-index"));
                this.selectOption(index);
                this.close();
            }
        });

        // 옵션 키보드 이벤트
        this.optionsList.addEventListener("keydown", (e) => {
            this.handleOptionKeydown(e);
        });

        // 외부 클릭 감지
        document.addEventListener("click", (e) => {
            if (!this.element.contains(e.target) && this.isOpen) {
                this.close();
            }
        });

        // 네이티브 select 변경 감지 (프로그래밍 방식 변경 대응)
        this.nativeSelect.addEventListener("change", () => {
            this.parseOptions();
            this.renderOptions();
            this.updateDisplay();
        });

        // 리사이즈 감지 (모바일/데스크톱 전환 및 드롭다운 위치 조절)
        let resizeTimer;
        window.addEventListener("resize", () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                const wasMobile = this.isMobile;
                this.isMobile = window.matchMedia("(max-width: 768px)").matches;

                if (wasMobile !== this.isMobile) {
                    // 모바일/데스크톱 전환 시 재초기화
                    if (this.isOpen) {
                        this.close();
                    }
                    this.init();
                } else if (!this.isMobile && this.isOpen) {
                    // 데스크톱에서 드롭다운이 열려있으면 위치 재조절
                    this.adjustDropdownPosition();
                }
            }, 100);
        });

        // 스크롤 시 드롭다운 위치 재조절
        if (!this.isMobile) {
            let scrollTimer;
            window.addEventListener(
                "scroll",
                () => {
                    if (!this.isOpen) return;

                    clearTimeout(scrollTimer);
                    scrollTimer = setTimeout(() => {
                        this.adjustDropdownPosition();
                    }, 50);
                },
                true
            );
        }
    }

    handleTriggerKeydown(e) {
        switch (e.key) {
            case "Enter":
            case " ":
                e.preventDefault();
                this.toggle();
                break;
            case "ArrowDown":
                e.preventDefault();
                this.open();
                this.focusNext();
                break;
            case "ArrowUp":
                e.preventDefault();
                this.open();
                this.focusPrevious();
                break;
            case "Escape":
                if (this.isOpen) {
                    e.preventDefault();
                    this.close();
                }
                break;
        }
    }

    handleOptionKeydown(e) {
        const options = Array.from(
            this.optionsList.querySelectorAll(
                ".no-form-select__option:not(.no-form-select__option--disabled)"
            )
        );

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                this.focusNext();
                break;
            case "ArrowUp":
                e.preventDefault();
                this.focusPrevious();
                break;
            case "Home":
                e.preventDefault();
                this.focusFirst();
                break;
            case "End":
                e.preventDefault();
                this.focusLast();
                break;
            case "Enter":
            case " ":
                e.preventDefault();
                const focused = this.optionsList.querySelector(
                    ".no-form-select__option--focused"
                );
                if (focused) {
                    const index = parseInt(focused.getAttribute("data-index"));
                    this.selectOption(index);
                    this.close();
                }
                break;
            case "Escape":
                e.preventDefault();
                this.close();
                this.trigger.focus();
                break;
            case "Tab":
                // 탭 키는 기본 동작 허용 (드롭다운 닫기)
                this.close();
                break;
        }
    }

    open() {
        if (this.isOpen) return;

        this.isOpen = true;
        this.element.classList.add("no-form-select--open");
        this.trigger.setAttribute("aria-expanded", "true");
        this.dropdown.setAttribute("aria-hidden", "false");

        // 모바일에서는 모달로 표시
        if (this.isMobile) {
            this.openModal();
        } else {
            // 데스크톱에서는 드롭다운 표시
            this.dropdown.style.display = "block";
            this.dropdown.classList.add("is-open");

            // 드롭다운 위치 자동 조절
            this.adjustDropdownPosition();

            // 포커스된 옵션으로 스크롤
            this.scrollToFocused();
        }

        // 외부 요소 탭 인덱스 비활성화
        const allFocusable = Array.from(
            document.querySelectorAll(
                'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((el) => !this.element.contains(el));
        this.tabIndexController.disable(allFocusable);
    }

    close() {
        if (!this.isOpen) return;

        this.isOpen = false;
        this.element.classList.remove("no-form-select--open");
        this.trigger.setAttribute("aria-expanded", "false");
        this.dropdown.setAttribute("aria-hidden", "true");

        if (this.isMobile) {
            this.closeModal();
        } else {
            this.dropdown.classList.remove("is-open");
            // transition이 끝난 후 display: none으로 변경
            setTimeout(() => {
                if (!this.isOpen) {
                    this.dropdown.style.display = "none";
                }
            }, 250);
        }

        // 외부 요소 탭 인덱스 복원
        this.tabIndexController.reset();
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    selectOption(index) {
        if (index < 0 || index >= this.options.length) return;
        if (this.options[index].disabled) return;

        this.selectedIndex = index;
        this.focusedIndex = index;

        // 네이티브 select 업데이트
        this.nativeSelect.selectedIndex = index;

        // change 이벤트 트리거
        const event = new Event("change", { bubbles: true });
        this.nativeSelect.dispatchEvent(event);

        // UI 업데이트
        this.updateDisplay();
        this.updateOptions();
    }

    updateDisplay() {
        if (
            this.selectedIndex >= 0 &&
            this.selectedIndex < this.options.length
        ) {
            const selectedOption = this.options[this.selectedIndex];
            this.valueDisplay.textContent = selectedOption.text;
        } else {
            this.valueDisplay.textContent =
                this.nativeSelect.options[0]?.textContent || "선택해주세요";
        }
    }

    updateOptions() {
        const optionElements = this.optionsList.querySelectorAll(
            ".no-form-select__option"
        );
        optionElements.forEach((el, index) => {
            el.classList.remove(
                "no-form-select__option--selected",
                "no-form-select__option--focused"
            );
            if (index === this.selectedIndex) {
                el.setAttribute("aria-selected", "true");
                el.classList.add("no-form-select__option--selected");
            } else {
                el.removeAttribute("aria-selected");
            }
            if (index === this.focusedIndex) {
                el.classList.add("no-form-select__option--focused");
            }
        });
    }

    focusNext() {
        const enabledOptions = this.options
            .map((opt, idx) => ({ opt, idx }))
            .filter(({ opt }) => !opt.disabled);

        const currentIndex = enabledOptions.findIndex(
            ({ idx }) => idx === this.focusedIndex
        );
        const nextIndex =
            currentIndex < enabledOptions.length - 1 ? currentIndex + 1 : 0;

        this.focusedIndex = enabledOptions[nextIndex].idx;
        this.updateOptions();
        this.scrollToFocused();
    }

    focusPrevious() {
        const enabledOptions = this.options
            .map((opt, idx) => ({ opt, idx }))
            .filter(({ opt }) => !opt.disabled);

        const currentIndex = enabledOptions.findIndex(
            ({ idx }) => idx === this.focusedIndex
        );
        const nextIndex =
            currentIndex > 0 ? currentIndex - 1 : enabledOptions.length - 1;

        this.focusedIndex = enabledOptions[nextIndex].idx;
        this.updateOptions();
        this.scrollToFocused();
    }

    focusFirst() {
        const firstEnabled = this.options.findIndex((opt) => !opt.disabled);
        if (firstEnabled >= 0) {
            this.focusedIndex = firstEnabled;
            this.updateOptions();
            this.scrollToFocused();
        }
    }

    focusLast() {
        let lastEnabled = -1;
        for (let i = this.options.length - 1; i >= 0; i--) {
            if (!this.options[i].disabled) {
                lastEnabled = i;
                break;
            }
        }
        if (lastEnabled >= 0) {
            this.focusedIndex = lastEnabled;
            this.updateOptions();
            this.scrollToFocused();
        }
    }

    scrollToFocused() {
        const focused = this.optionsList.querySelector(
            ".no-form-select__option--focused"
        );
        if (focused) {
            focused.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    }

    adjustDropdownPosition() {
        if (this.isMobile || !this.dropdown) return;

        // 드롭다운이 열린 상태에서만 위치 조절
        if (!this.isOpen) return;

        const rect = this.element.getBoundingClientRect();
        const dropdownHeight = this.dropdown.offsetHeight || 200; // 기본값
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // 기본 위치 (아래)
        this.dropdown.style.top = "calc(100% + 0.4rem)";
        this.dropdown.style.bottom = "auto";
        this.dropdown.style.transform = "translateY(0)";
        this.dropdown.classList.remove("no-form-select__dropdown--up");

        // 아래 공간이 부족하고 위 공간이 더 많으면 위로 표시
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            this.dropdown.style.top = "auto";
            this.dropdown.style.bottom = "calc(100% + 0.4rem)";
            this.dropdown.style.transform = "translateY(0)";
            this.dropdown.classList.add("no-form-select__dropdown--up");
        }
    }

    openModal() {
        // 기존 모달이 있으면 제거
        if (this.modalOverlay && this.modalOverlay.parentNode) {
            this.modalOverlay.parentNode.removeChild(this.modalOverlay);
        }

        // 모달 오버레이 생성
        const overlay = document.createElement("div");
        overlay.className = "no-form-select__modal-overlay";
        overlay.setAttribute("data-select-modal-overlay", "");
        overlay.style.display = "flex"; // 모바일에서도 표시되도록 명시

        // 모달 컨테이너
        const modal = document.createElement("div");
        modal.className = "no-form-select__modal";
        modal.setAttribute("role", "dialog");
        modal.setAttribute("aria-modal", "true");
        modal.setAttribute("aria-label", "시술 선택");

        // 모달 헤더
        const header = document.createElement("div");
        header.className = "no-form-select__modal-header";
        const title = document.createElement("h3");
        title.className = "no-form-select__modal-title";
        title.textContent = "시술 선택";
        const closeBtn = document.createElement("button");
        closeBtn.className = "no-form-select__modal-close";
        closeBtn.setAttribute("aria-label", "닫기");
        closeBtn.innerHTML = '<i class="fa-regular fa-xmark"></i>';
        closeBtn.addEventListener("click", () => this.close());
        header.appendChild(title);
        header.appendChild(closeBtn);

        // 모달 바디 (옵션 리스트)
        const body = document.createElement("div");
        body.className = "no-form-select__modal-body";
        body.setAttribute("data-lenis-prevent", "");
        
        // 옵션 리스트 복제 (깊은 복사)
        const clonedList = this.optionsList.cloneNode(true);
        body.appendChild(clonedList);

        modal.appendChild(header);
        modal.appendChild(body);
        overlay.appendChild(modal);

        // DOM에 추가 (다른 모달 위에 표시되도록)
        document.body.appendChild(overlay);
        document.body.style.overflow = "hidden";

        // z-index 설정 (Modal.DEPTH.MEDIUM과 동일)
        overlay.style.zIndex = "10050";

        // 애니메이션을 위해 약간의 지연 후 is-open 클래스 추가
        requestAnimationFrame(() => {
            setTimeout(() => {
                overlay.classList.add("is-open");
            }, 10);
        });

        // 모달 내 옵션 클릭 이벤트 (이벤트 위임 사용)
        body.addEventListener("click", (e) => {
            const option = e.target.closest(".no-form-select__option");
            if (option && !option.classList.contains("no-form-select__option--disabled")) {
                e.preventDefault();
                e.stopPropagation();
                const index = parseInt(option.getAttribute("data-index"));
                if (!isNaN(index)) {
                    this.selectOption(index);
                    this.close();
                }
            }
        });

        // ESC 키로 닫기
        const handleEscape = (e) => {
            if (e.key === "Escape") {
                this.close();
                document.removeEventListener("keydown", handleEscape);
            }
        };
        document.addEventListener("keydown", handleEscape);

        // 오버레이 클릭으로 닫기
        overlay.addEventListener("click", (e) => {
            // 오버레이 자체를 클릭했을 때만 닫기 (모달 내부 클릭은 제외)
            if (e.target === overlay) {
                this.close();
            }
        });

        // 모달 내부 요소에 탭 인덱스 설정
        const modalFocusable = body.querySelectorAll(
            ".no-form-select__option:not(.no-form-select__option--disabled)"
        );
        modalFocusable.forEach((el, index) => {
            el.setAttribute("tabindex", index === 0 ? "0" : "-1");
        });

        // 첫 번째 옵션에 포커스
        if (modalFocusable.length > 0) {
            setTimeout(() => {
                const focusedOption = body.querySelector(
                    ".no-form-select__option--focused"
                );
                if (focusedOption) {
                    focusedOption.focus();
                } else {
                    modalFocusable[0].focus();
                }
            }, 100);
        }

        this.modalOverlay = overlay;
    }

    closeModal() {
        if (this.modalOverlay) {
            // 애니메이션을 위해 is-open 클래스 먼저 제거
            this.modalOverlay.classList.remove("is-open");

            // 애니메이션 완료 후 제거
            setTimeout(() => {
                if (this.modalOverlay && this.modalOverlay.parentNode) {
                    this.modalOverlay.parentNode.removeChild(this.modalOverlay);
                }
                document.body.style.overflow = "";
                this.modalOverlay = null;
            }, 300);
        }
    }

    destroy() {
        this.close();
        this.tabIndexController.reset();
        // 이벤트 리스너 제거는 필요시 구현
    }
}
